import { useState, useRef, useCallback } from 'react';
import {
  useImportShowroom,
  useConfirmShowroom,
  useShowroomRecords,
} from '@saiens/shared/hooks';
import { toast } from '@saiens/shared/stores';
import { compressImage } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  Camera,
  ImagePlus,
  Upload,
  Check,
  X,
  Pencil,
  ArrowLeft,
  User,
  Phone,
  MapPin,
  Plus,
} from 'lucide-react';
import type {
  ShowroomOcrResult,
  ShowroomConfirmResult,
} from '@saiens/shared/types';

type PageStep = 'select' | 'preview' | 'success';
type ImageSide = 'front' | 'back';

// Multi-select options
const INFO_SOURCE_OPTIONS = [
  'FB', 'YouTube', '設計師推薦', '廚具商推薦',
  '網站搜尋', '朋友推薦', '其他',
];

const CONSTRUCTION_NEEDS_OPTIONS = [
  '廚房檯面', '中島', '衛浴檯面', '包浴缸',
  '邊櫃', '餐桌/傢俱', '牆面', '地面', '其他',
];

const HOUSE_STATUS_OPTIONS = [
  { value: 'presale', label: '預售屋' },
  { value: 'new_shell', label: '新成毛胚屋' },
  { value: 'demolish_renovate', label: '舊屋拆除翻新' },
  { value: 'minor_adjust', label: '既有住宅微調' },
];

const PROJECT_STAGE_OPTIONS = [
  { value: 'planning', label: '規劃階段' },
  { value: 'demolish', label: '拆除階段' },
  { value: 'woodwork_paint', label: '木作泥作油漆階段' },
  { value: 'kitchen_cabinet', label: '廚具/系統櫃階段' },
  { value: 'other', label: '其他' },
];

// Map OCR text to selection values
const HOUSE_STATUS_TEXT_MAP: Record<string, string> = {
  '預售屋': 'presale',
  '新成毛胚屋': 'new_shell',
  '舊屋拆除翻新': 'demolish_renovate',
  '既有住宅微調': 'minor_adjust',
};

const PROJECT_STAGE_TEXT_MAP: Record<string, string> = {
  '規劃階段': 'planning',
  '規劃': 'planning',
  '拆除階段': 'demolish',
  '拆除': 'demolish',
  '木作泥作油漆階段': 'woodwork_paint',
  '木作泥作油漆': 'woodwork_paint',
  '廚具/系統櫃階段': 'kitchen_cabinet',
  '廚具系統櫃階段': 'kitchen_cabinet',
  '廚具/系統櫃': 'kitchen_cabinet',
  '其他': 'other',
};

function mapHouseStatus(text: string): string {
  for (const [label, key] of Object.entries(HOUSE_STATUS_TEXT_MAP)) {
    if (text.includes(label)) return key;
  }
  return '';
}

function mapProjectStage(text: string): string {
  for (const [label, key] of Object.entries(PROJECT_STAGE_TEXT_MAP)) {
    if (text.includes(label)) return key;
  }
  return '';
}

function toggleCsvItem(csv: string, item: string): string {
  const items = csv ? csv.split(',').map((s) => s.trim()).filter(Boolean) : [];
  const idx = items.indexOf(item);
  if (idx >= 0) {
    return [...items.slice(0, idx), ...items.slice(idx + 1)].join(',');
  }
  return [...items, item].join(',');
}

function csvIncludes(csv: string, item: string): boolean {
  if (!csv) return false;
  return csv.split(',').map((s) => s.trim()).includes(item);
}

export function ShowroomImportPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<PageStep>('select');
  const [frontImagePreview, setFrontImagePreview] = useState<string | null>(null);
  const [frontImageBase64, setFrontImageBase64] = useState<string | null>(null);
  const [backImagePreview, setBackImagePreview] = useState<string | null>(null);
  const [backImageBase64, setBackImageBase64] = useState<string | null>(null);
  const activeInputRef = useRef<ImageSide>('front');
  const [ocrResult, setOcrResult] = useState<ShowroomOcrResult | null>(null);
  const [confirmResult, setConfirmResult] = useState<ShowroomConfirmResult | null>(null);
  const [createLead, setCreateLead] = useState(false);

  // Mapped selection values (separate from raw OCR text)
  const [houseStatus, setHouseStatus] = useState('');
  const [projectStage, setProjectStage] = useState('');

  const { data: recentRecords, isLoading: recordsLoading } = useShowroomRecords(10);
  const importShowroom = useImportShowroom();
  const confirmShowroom = useConfirmShowroom();

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('檔案格式錯誤', '請選擇圖片檔案');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('檔案過大', '圖片大小不能超過 10MB');
      return;
    }

    const side = activeInputRef.current;

    try {
      const { base64, previewUrl } = await compressImage(file);

      if (side === 'front') {
        setFrontImagePreview(previewUrl);
        setFrontImageBase64(base64);
        setOcrResult(null);
        setConfirmResult(null);
      } else {
        setBackImagePreview(previewUrl);
        setBackImageBase64(base64);
      }
    } catch {
      toast.error('處理失敗', '無法處理圖片');
    }

    e.target.value = '';
  }, []);

  const handleCameraCapture = (side: ImageSide) => {
    activeInputRef.current = side;
    cameraInputRef.current?.click();
  };

  const handleGallerySelect = (side: ImageSide) => {
    activeInputRef.current = side;
    fileInputRef.current?.click();
  };

  const handleClearFrontImage = () => {
    setFrontImagePreview(null);
    setFrontImageBase64(null);
    setOcrResult(null);
    setStep('select');
  };

  const handleClearBackImage = () => {
    setBackImagePreview(null);
    setBackImageBase64(null);
  };

  const handleStartOcr = () => {
    if (!frontImageBase64) return;

    importShowroom.mutate(
      {
        image_base64: frontImageBase64,
        back_image_base64: backImageBase64 || undefined,
      },
      {
        onSuccess: (data) => {
          const ocr = data.ocr_result;
          setOcrResult(ocr);
          setHouseStatus(mapHouseStatus(ocr.house_status));
          setProjectStage(mapProjectStage(ocr.project_stage));
          setStep('preview');
        },
      }
    );
  };

  const handleOcrFieldChange = (field: keyof ShowroomOcrResult, value: string) => {
    if (!ocrResult) return;
    setOcrResult({ ...ocrResult, [field]: value });
  };

  const handleConfirmImport = () => {
    if (!ocrResult || !frontImageBase64) return;

    // Merge mapped selection values back into OCR result for backend
    const finalResult = {
      ...ocrResult,
      house_status: HOUSE_STATUS_OPTIONS.find((o) => o.value === houseStatus)?.label || ocrResult.house_status,
      project_stage: PROJECT_STAGE_OPTIONS.find((o) => o.value === projectStage)?.label || ocrResult.project_stage,
    };

    confirmShowroom.mutate(
      {
        ocr_result: finalResult,
        image_base64: frontImageBase64,
        back_image_base64: backImageBase64 || undefined,
        create_lead: createLead,
      },
      {
        onSuccess: (data) => {
          setConfirmResult(data);
          setStep('success');
        },
      }
    );
  };

  const handleBackToSelect = () => {
    setOcrResult(null);
    setStep('select');
  };

  const handleNewScan = () => {
    setFrontImagePreview(null);
    setFrontImageBase64(null);
    setBackImagePreview(null);
    setBackImageBase64(null);
    setOcrResult(null);
    setConfirmResult(null);
    setCreateLead(false);
    setHouseStatus('');
    setProjectStage('');
    setStep('select');
  };

  const hasFrontImage = Boolean(frontImagePreview);

  return (
    <div className="pb-24 px-4 py-6">
      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
        aria-label="Camera capture"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        aria-label="Gallery select"
      />

      {/* Header */}
      <div className="mb-6">
        {step === 'preview' && (
          <button
            onClick={handleBackToSelect}
            className="mb-2 flex items-center gap-1 text-sm text-gray-500"
          >
            <ArrowLeft className="h-4 w-4" />
            返回
          </button>
        )}
        <h1 className="text-xl font-bold text-gray-900">展間接待</h1>
        <p className="mt-1 text-sm text-gray-500">
          {step === 'select' && '拍照手寫接待表，AI 自動辨識填入系統'}
          {step === 'preview' && '確認辨識結果，可直接編輯修正'}
          {step === 'success' && '記錄已儲存'}
        </p>
      </div>

      {/* === Step: Success === */}
      {step === 'success' && confirmResult && (
        <div className="mb-6 rounded-xl bg-green-50 p-4">
          <div className="flex items-center gap-2 text-green-700">
            <Check className="h-5 w-5" />
            <span className="font-medium">接待記錄已儲存</span>
          </div>
          <div className="mt-3 rounded-lg bg-white p-3 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">
                  {confirmResult.customer_name}
                </h3>
                <p className="text-sm text-gray-500">{confirmResult.name}</p>
                {confirmResult.phone && (
                  <p className="mt-1 text-sm text-gray-500">{confirmResult.phone}</p>
                )}
                {confirmResult.lead_id && (
                  <p className="mt-1 text-xs text-green-600">
                    已建立 CRM 案件: {confirmResult.lead_name}
                  </p>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={handleNewScan}
            className="mt-4 w-full rounded-lg border border-green-600 py-2 text-center text-sm font-medium text-green-600"
          >
            繼續掃描新接待表
          </button>
        </div>
      )}

      {/* === Step: OCR Preview & Edit === */}
      {step === 'preview' && ocrResult && (
        <div className="mb-6">
          {/* Image thumbnails */}
          <div className="mb-4 flex gap-2">
            {frontImagePreview && (
              <div className="flex-1 rounded-lg bg-gray-100 p-2">
                <p className="mb-1 text-center text-xs text-gray-500">正面</p>
                <img
                  src={frontImagePreview}
                  alt="接待表正面"
                  className="mx-auto rounded-lg object-contain"
                  style={{ maxHeight: '120px' }}
                />
              </div>
            )}
            {backImagePreview && (
              <div className="flex-1 rounded-lg bg-gray-100 p-2">
                <p className="mb-1 text-center text-xs text-gray-500">反面</p>
                <img
                  src={backImagePreview}
                  alt="接待表反面"
                  className="mx-auto rounded-lg object-contain"
                  style={{ maxHeight: '120px' }}
                />
              </div>
            )}
          </div>

          {/* Editable OCR fields */}
          <div className="rounded-xl bg-white shadow-sm">
            <div className="flex items-center gap-2 border-b px-4 py-3">
              <Pencil className="h-4 w-4 text-gray-500" />
              <h3 className="font-medium text-gray-900">辨識結果</h3>
            </div>
            <div className="divide-y">
              {/* Basic text fields */}
              {([
                ['receptionist', '接待人員'],
                ['reception_date', '接待時間'],
                ['customer_name', '顧客姓名'],
                ['phone', '電話/LINE'],
                ['site_address', '案場地點'],
              ] as const).map(([field, label]) => (
                <div key={field} className="flex items-center gap-3 px-4 py-2.5">
                  <label className="w-20 flex-shrink-0 text-xs text-gray-500">
                    {label}
                  </label>
                  <input
                    type="text"
                    value={ocrResult[field]}
                    onChange={(e) => handleOcrFieldChange(field, e.target.value)}
                    className="flex-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder={`輸入${label}`}
                  />
                </div>
              ))}

              {/* House Status - Select */}
              <div className="flex items-center gap-3 px-4 py-2.5">
                <label className="w-20 flex-shrink-0 text-xs text-gray-500">
                  房屋狀態
                </label>
                <select
                  value={houseStatus}
                  onChange={(e) => setHouseStatus(e.target.value)}
                  className="flex-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">-- 請選擇 --</option>
                  {HOUSE_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Project Stage - Select */}
              <div className="flex items-center gap-3 px-4 py-2.5">
                <label className="w-20 flex-shrink-0 text-xs text-gray-500">
                  專案階段
                </label>
                <select
                  value={projectStage}
                  onChange={(e) => setProjectStage(e.target.value)}
                  className="flex-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">-- 請選擇 --</option>
                  {PROJECT_STAGE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {projectStage === 'other' && (
                <div className="flex items-center gap-3 px-4 py-2.5">
                  <label className="w-20 flex-shrink-0 text-xs text-gray-500">
                    其他說明
                  </label>
                  <input
                    type="text"
                    value={ocrResult.project_stage_other}
                    onChange={(e) => handleOcrFieldChange('project_stage_other', e.target.value)}
                    className="flex-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              )}

              {/* More text fields */}
              {([
                ['design_company', '設計公司'],
                ['kitchen_company', '廚具公司'],
                ['expected_entry_date', '預計進場'],
                ['other_brands', '評估品牌'],
              ] as const).map(([field, label]) => (
                <div key={field} className="flex items-center gap-3 px-4 py-2.5">
                  <label className="w-20 flex-shrink-0 text-xs text-gray-500">
                    {label}
                  </label>
                  <input
                    type="text"
                    value={ocrResult[field]}
                    onChange={(e) => handleOcrFieldChange(field, e.target.value)}
                    className="flex-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder={`輸入${label}`}
                  />
                </div>
              ))}

              {/* Info Source - Checkbox Group */}
              <div className="px-4 py-2.5">
                <label className="mb-2 block text-xs text-gray-500">資訊來源</label>
                <div className="flex flex-wrap gap-2">
                  {INFO_SOURCE_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() =>
                        handleOcrFieldChange('info_source', toggleCsvItem(ocrResult.info_source, opt))
                      }
                      className={`rounded-full px-3 py-1 text-xs transition-colors ${
                        csvIncludes(ocrResult.info_source, opt)
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Construction Needs - Checkbox Group */}
              <div className="px-4 py-2.5">
                <label className="mb-2 block text-xs text-gray-500">施作需求</label>
                <div className="flex flex-wrap gap-2">
                  {CONSTRUCTION_NEEDS_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() =>
                        handleOcrFieldChange('construction_needs', toggleCsvItem(ocrResult.construction_needs, opt))
                      }
                      className={`rounded-full px-3 py-1 text-xs transition-colors ${
                        csvIncludes(ocrResult.construction_needs, opt)
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Textarea fields */}
              {([
                ['interested_patterns', '花色'],
                ['reception_notes', '接待筆記'],
                ['follow_up', '後續跟進'],
              ] as const).map(([field, label]) => (
                <div key={field} className="px-4 py-2.5">
                  <label className="mb-1 block text-xs text-gray-500">{label}</label>
                  <textarea
                    value={ocrResult[field]}
                    onChange={(e) => handleOcrFieldChange(field, e.target.value)}
                    rows={2}
                    className="w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder={`輸入${label}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Create CRM Lead toggle */}
          <div className="mt-4 flex items-center justify-between rounded-xl bg-white px-4 py-3 shadow-sm">
            <div className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-700">同時建立 CRM 案件</span>
            </div>
            <button
              type="button"
              onClick={() => setCreateLead(!createLead)}
              className={`relative h-6 w-11 rounded-full transition-colors ${
                createLead ? 'bg-primary' : 'bg-gray-200'
              }`}
            >
              <span
                className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                  createLead ? 'translate-x-5' : ''
                }`}
              />
            </button>
          </div>
        </div>
      )}

      {/* === Step: Select Image === */}
      {step === 'select' && (
        <>
          {/* Front image upload area */}
          <div className="mb-4">
            {!frontImagePreview ? (
              <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 p-6">
                <div className="flex flex-col items-center">
                  <ImagePlus className="h-10 w-10 text-gray-400" />
                  <p className="mt-2 text-center text-sm font-medium text-gray-700">
                    接待表正面
                  </p>
                  <p className="mt-1 text-center text-xs text-gray-400">
                    拍攝或選擇手寫接待表照片
                  </p>
                  <div className="mt-3 flex gap-3">
                    <button
                      onClick={() => handleCameraCapture('front')}
                      aria-label="拍攝接待表正面"
                      className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white"
                    >
                      <Camera className="h-4 w-4" />
                      拍照
                    </button>
                    <button
                      onClick={() => handleGallerySelect('front')}
                      aria-label="從相簿選擇正面"
                      className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700"
                    >
                      <Upload className="h-4 w-4" />
                      相簿
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative rounded-xl bg-gray-100 p-2">
                <p className="mb-1 text-center text-xs font-medium text-gray-500">接待表正面</p>
                <img
                  src={frontImagePreview}
                  alt="接待表正面"
                  className="w-full rounded-lg object-contain"
                  style={{ maxHeight: '250px' }}
                />
                <button
                  onClick={handleClearFrontImage}
                  aria-label="清除正面圖片"
                  className="absolute right-3 top-3 rounded-full bg-black/50 p-1.5 text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {/* Back image upload area (optional) */}
          <div className="mb-6">
            {!backImagePreview ? (
              <div className="rounded-xl border-2 border-dashed border-gray-100 bg-gray-50/50 p-6">
                <div className="flex flex-col items-center">
                  <ImagePlus className="h-8 w-8 text-gray-300" />
                  <p className="mt-2 text-center text-sm font-medium text-gray-500">
                    接待表反面（選填）
                  </p>
                  <p className="mt-1 text-center text-xs text-gray-400">
                    如有反面資訊可一併上傳
                  </p>
                  <div className="mt-3 flex gap-3">
                    <button
                      onClick={() => handleCameraCapture('back')}
                      aria-label="拍攝接待表反面"
                      className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600"
                    >
                      <Camera className="h-3.5 w-3.5" />
                      拍照
                    </button>
                    <button
                      onClick={() => handleGallerySelect('back')}
                      aria-label="從相簿選擇反面"
                      className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      相簿
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative rounded-xl bg-gray-100 p-2">
                <p className="mb-1 text-center text-xs font-medium text-gray-500">接待表反面</p>
                <img
                  src={backImagePreview}
                  alt="接待表反面"
                  className="w-full rounded-lg object-contain"
                  style={{ maxHeight: '200px' }}
                />
                <button
                  onClick={handleClearBackImage}
                  aria-label="清除反面圖片"
                  className="absolute right-3 top-3 rounded-full bg-black/50 p-1.5 text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {/* Recent Records */}
          {!hasFrontImage && (
            <div className="mb-6">
              <h2 className="mb-3 font-semibold text-gray-900">最近記錄</h2>
              {recordsLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : recentRecords && recentRecords.length > 0 ? (
                <div className="space-y-2">
                  {recentRecords.map((record) => (
                    <div
                      key={record.id}
                      className="flex items-center gap-3 rounded-lg bg-white p-3 shadow-sm"
                    >
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-cyan-50">
                        <User className="h-5 w-5 text-cyan-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="truncate font-medium text-gray-900">
                            {record.customer_name}
                          </h3>
                          <span className="flex-shrink-0 text-xs text-gray-400">
                            {record.name}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                          {record.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {record.phone}
                            </span>
                          )}
                          {record.site_address && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {record.site_address}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-lg bg-gray-50 py-8 text-center text-sm text-gray-500">
                  尚無接待記錄
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Bottom Action Buttons */}
      {step === 'select' && hasFrontImage && (
        <div className="fixed bottom-20 left-0 right-0 z-40 border-t bg-white p-4">
          <button
            onClick={handleStartOcr}
            disabled={importShowroom.isPending || !frontImageBase64}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 font-medium text-white hover:bg-primary/90 disabled:bg-gray-300"
          >
            {importShowroom.isPending ? (
              <>
                <LoadingSpinner size="sm" />
                辨識中...
              </>
            ) : (
              <>
                <Upload className="h-5 w-5" />
                開始辨識
              </>
            )}
          </button>
        </div>
      )}

      {step === 'preview' && (
        <div className="fixed bottom-20 left-0 right-0 z-40 border-t bg-white p-4">
          <div className="flex gap-3">
            <button
              onClick={handleBackToSelect}
              disabled={confirmShowroom.isPending}
              className="flex-1 rounded-xl border border-gray-300 py-3 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              取消
            </button>
            <button
              onClick={handleConfirmImport}
              disabled={confirmShowroom.isPending || !ocrResult?.customer_name}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary py-3 font-medium text-white hover:bg-primary/90 disabled:bg-gray-300"
            >
              {confirmShowroom.isPending ? (
                <>
                  <LoadingSpinner size="sm" />
                  儲存中...
                </>
              ) : (
                <>
                  <Check className="h-5 w-5" />
                  確認儲存
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
